import { useMemo } from "react";

/* ──────────────────────────────────────────────────────────
   Lightweight SVG chart components for dashboard apps.
   No external dependencies — pure React + SVG.
   ────────────────────────────────────────────────────────── */

// ── Shared color palette (matches WebAdmin2 chart colors) ──
const DEFAULT_COLORS = [
  "#E4002B", "#001A72", "#25282A", "#00B8DD", "#00A651",
  "#FFB114", "#8246AF", "#FF9BB1", "#0078D0", "#E8E3DC",
  "#4DD4EC", "#40C878", "#A878CC", "#FFCB5C", "#FF4D6A",
];

export function getColor(index: number, explicit?: string): string {
  if (explicit) return explicit;
  return DEFAULT_COLORS[index % DEFAULT_COLORS.length]!;
}

const MAX_LABEL_LEN = 20;
function truncLabel(s: string): string {
  return s.length > MAX_LABEL_LEN ? s.slice(0, MAX_LABEL_LEN - 1) + "…" : s;
}

/** Format large numbers: 1500000 → "1.5M", 250000 → "250K", 1200 → "1.2K" */
function formatCompact(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000) {
    const m = abs / 1_000_000;
    return sign + m.toFixed(1).replace(/\.0$/, "") + "M";
  }
  if (abs >= 1_000) {
    const k = abs / 1_000;
    return sign + k.toFixed(1).replace(/\.0$/, "") + "K";
  }
  return sign + String(Math.round(abs));
}

/** Round max up to a "nice" even number for Y-axis labels (e.g. 1900805 → 2000000). */
export function niceRound(value: number): number {
  if (value === 0) return 1;
  const abs = Math.abs(value);
  if (abs < 1e-10) return value >= 0 ? 1 : -1;
  const magnitude = Math.pow(10, Math.floor(Math.log10(abs)));
  const fraction = abs / magnitude;
  const nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;
  return nice * magnitude * (value >= 0 ? 1 : -1);
}

// ── Donut Chart ──────────────────────────────────────────

export interface DonutSlice {
  label: string;
  value: number;
  color?: string;
  tooltip?: string;
}

interface DonutChartProps {
  data: DonutSlice[];
  size?: number;
  innerRadius?: number;
  showSegmentLabels?: boolean;
  textSize?: number | null;
  formatSegmentLabel?: (label: string, value: number) => string;
}

export function DonutChart({ data, size = 200, innerRadius = 60, showSegmentLabels = false, textSize = null, formatSegmentLabel }: DonutChartProps) {
  const radius = size / 2 - 4;
  const cx = size / 2;
  const cy = size / 2;
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return null;

  const slices = useMemo(() => {
    let angle = -Math.PI / 2;
    return data.map((d, i) => {
      const fraction = d.value / total;
      const startAngle = angle;
      const endAngle = angle + fraction * Math.PI * 2;
      angle = endAngle;
      const largeArc = fraction > 0.5 ? 1 : 0;

      const x1 = cx + radius * Math.cos(startAngle);
      const y1 = cy + radius * Math.sin(startAngle);
      const x2 = cx + radius * Math.cos(endAngle - 0.001);
      const y2 = cy + radius * Math.sin(endAngle - 0.001);
      const ix1 = cx + innerRadius * Math.cos(startAngle);
      const iy1 = cy + innerRadius * Math.sin(startAngle);
      const ix2 = cx + innerRadius * Math.cos(endAngle - 0.001);
      const iy2 = cy + innerRadius * Math.sin(endAngle - 0.001);

      const path = [
        `M ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`,
        `L ${ix2} ${iy2}`,
        `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix1} ${iy1}`,
        "Z",
      ].join(" ");

      return { path, color: getColor(i, d.color), tooltip: d.tooltip ?? `${d.label}: ${d.value}`, label: d.label, value: d.value, midAngle: (startAngle + endAngle) / 2 };
    });
  }, [data, total, cx, cy, radius, innerRadius]);

  // For outside labels: extend beyond the donut radius
  const outerLabelRadius = radius + 20;
  const lineEndRadius = radius + 6;
  const lineMidRadius = (radius + innerRadius) / 2 + (radius - innerRadius) / 4;
  const fontSize = textSize ?? Math.max(12, size * 0.056);
  const labelHeight = fontSize + 4;
  const fmtLabel = formatSegmentLabel ?? ((l: string, v: number) => `${l} ${v}`);
  const maxLabelTextLen = showSegmentLabels ? Math.max(...data.map(d => fmtLabel(d.label, d.value).length), 0) : 0;
  const svgPad = showSegmentLabels ? Math.max(100, maxLabelTextLen * 7) : 0;
  const svgPadY = showSegmentLabels ? 30 : 0;
  const svgW = size + svgPad * 2;
  const svgH = size + svgPadY * 2;
  const offsetX = svgPad;
  const offsetY = svgPadY;

  // Resolve label positions to avoid overlap
  const labelPositions = useMemo(() => {
    if (!showSegmentLabels) return [];
    const raw = slices.map((s) => {
      const natural = cy + outerLabelRadius * Math.sin(s.midAngle);
      const isRight = Math.cos(s.midAngle) >= 0;
      return { natural, y: natural, isRight };
    });

    // Spread overlapping labels on each side independently
    for (const side of [true, false]) {
      const indices = raw
        .map((r, i) => ({ i, r }))
        .filter(({ r }) => r.isRight === side)
        .sort((a, b) => a.r.natural - b.r.natural);

      for (let pass = 0; pass < 5; pass++) {
        let moved = false;
        for (let j = 1; j < indices.length; j++) {
          const prev = raw[indices[j - 1]!.i]!;
          const curr = raw[indices[j]!.i]!;
          const overlap = prev.y + labelHeight - curr.y;
          if (overlap > 0) {
            prev.y -= overlap / 2;
            curr.y += overlap / 2;
            moved = true;
          }
        }
        if (!moved) break;
      }
    }
    return raw;
  }, [slices, showSegmentLabels, cy, outerLabelRadius, labelHeight]);

  return (
    <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`}>
      <g transform={`translate(${offsetX}, ${offsetY})`}>
        {slices.map((s, i) => (
          <g key={i}>
            <path d={s.path} fill={s.color}>
              <title>{s.tooltip}</title>
            </path>
            {showSegmentLabels && labelPositions[i] && (
              <>
                <line
                  x1={cx + lineMidRadius * Math.cos(s.midAngle)}
                  y1={cy + lineMidRadius * Math.sin(s.midAngle)}
                  x2={cx + lineEndRadius * Math.cos(s.midAngle)}
                  y2={cy + lineEndRadius * Math.sin(s.midAngle)}
                  stroke="var(--color-text-muted)"
                  strokeWidth={1}
                />
                <line
                  x1={cx + lineEndRadius * Math.cos(s.midAngle)}
                  y1={cy + lineEndRadius * Math.sin(s.midAngle)}
                  x2={labelPositions[i].isRight ? cx + outerLabelRadius - 4 : cx - outerLabelRadius + 4}
                  y2={labelPositions[i].y}
                  stroke="var(--color-text-muted)"
                  strokeWidth={1}
                />
                <text
                  x={labelPositions[i].isRight ? cx + outerLabelRadius : cx - outerLabelRadius}
                  y={labelPositions[i].y}
                  textAnchor={labelPositions[i].isRight ? "start" : "end"}
                  dominantBaseline="central"
                  fill="var(--color-text)"
                  fontSize={fontSize}
                  fontWeight={600}
                  pointerEvents="none"
                >
                  {fmtLabel(s.label, s.value)}
                </text>
              </>
            )}
          </g>
        ))}
      </g>
    </svg>
  );
}

// ── Bar Chart ────────────────────────────────────────────

export interface BarDatum {
  label: string;
  value: number;
  color?: string;
  tooltip?: string;
}

interface BarChartProps {
  data: BarDatum[];
  width?: number;
  height?: number;
  color?: string;
  formatValue?: (v: number) => string;
}

export function BarChart({ data, width = 500, height = 250, color, formatValue }: BarChartProps) {
  if (data.length === 0) return null;
  const max = Math.max(...data.map(d => d.value), 1);
  const topPad = 22;
  const plotLeft = 55;
  const gap = 6;
  const barWidth = Math.max(8, (width - plotLeft - 10) / data.length - gap);
  const maxLabelLen = Math.max(...data.map(d => truncLabel(d.label).length), 0);
  const rotateLabels = maxLabelLen * 6 > barWidth + gap;
  const rotatedLabelH = rotateLabels ? Math.ceil(maxLabelLen * 5.5 * Math.sin(Math.PI * 35 / 180)) + 8 : 0;
  const bottomPad = rotateLabels ? 14 + rotatedLabelH : 40;
  const chartH = height + topPad + (rotateLabels ? rotatedLabelH - 20 : 0);
  const plotH = chartH - bottomPad;
  const chartW = width;

  // Round Y-axis to nice even steps
  const niceMax = niceRound(max);

  return (
    <svg width={chartW} height={chartH} viewBox={`0 0 ${chartW} ${chartH}`} style={{ display: "block" }}>
      {/* Y axis */}
      <line x1={50} y1={topPad} x2={50} y2={plotH} stroke="var(--color-border-default)" />
      {[0, 0.25, 0.5, 0.75, 1].map(f => {
        const y = plotH - f * (plotH - topPad);
        const val = Math.round(niceMax * f);
        return (
          <g key={f}>
            <line x1={48} y1={y} x2={chartW} y2={y} stroke="var(--color-bg-muted)" />
            <text x={46} y={y + 4} textAnchor="end" fontSize={15} fill="var(--color-text-muted)">{val}</text>
          </g>
        );
      })}
      {/* Bars */}
      {data.map((d, i) => {
        const barH = (d.value / niceMax) * (plotH - topPad);
        const x = plotLeft + i * (barWidth + gap);
        const y = plotH - barH;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barWidth} height={barH} fill={d.color ?? color ?? getColor(0)} rx={2}>
              <title>{d.tooltip ?? `${d.label}: ${d.value}`}</title>
            </rect>
            <text
              x={x + barWidth / 2}
              y={y - 4}
              textAnchor="middle"
              fontSize={13}
              fontWeight={700}
              fill="var(--color-text)"
            >
              {formatValue ? formatValue(d.value) : String(d.value)}
            </text>
            <text
              x={x + barWidth / 2}
              y={plotH + 12}
              textAnchor={rotateLabels ? "end" : "middle"}
              fontSize={13}
              fill="var(--color-text-muted)"
              transform={rotateLabels ? `rotate(-35, ${x + barWidth / 2}, ${plotH + 12})` : undefined}
            >
              {truncLabel(d.label)}
              <title>{d.label}</title>
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ── Stacked Bar Chart ────────────────────────────────────

export interface StackedBarDatum {
  label: string;
  tooltip?: string;
  segments: { value: number; color: string; tooltip?: string }[];
}

interface StackedBarChartProps {
  data: StackedBarDatum[];
  seriesLabels?: string[];
  width?: number;
  height?: number;
}

export function StackedBarChart({ data, width = 500, height = 250 }: StackedBarChartProps) {
  if (data.length === 0) return null;
  const max = Math.max(...data.map(d => d.segments.reduce((s, seg) => s + seg.value, 0)), 1);
  const barWidth = Math.max(8, Math.min(40, (width - 60) / data.length - 4));
  const chartW = data.length * (barWidth + 4) + 60;
  const chartH = height;
  const plotH = chartH - 40;

  return (
    <svg width={chartW} height={chartH} viewBox={`0 0 ${chartW} ${chartH}`} style={{ display: "block" }}>
      <line x1={50} y1={5} x2={50} y2={plotH} stroke="var(--color-border-default)" />
      {data.map((d, i) => {
        const x = 55 + i * (barWidth + 4);
        let cumY = plotH;
        return (
          <g key={i}>
            {d.segments.map((seg, si) => {
              const segH = (seg.value / max) * (plotH - 10);
              cumY -= segH;
              return (
                <rect key={si} x={x} y={cumY} width={barWidth} height={segH} fill={seg.color} rx={1}>
                  <title>{d.tooltip ?? seg.tooltip ?? `${seg.value}`}</title>
                </rect>
              );
            })}
            <text x={x + barWidth / 2} y={plotH + 14} textAnchor="middle" fontSize={15} fill="var(--color-text-muted)">
              {d.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ── Line / Area Chart ────────────────────────────────────

export interface LineDataPoint {
  label: string;
  value: number;
  tooltip?: string;
}

export interface LineSeries {
  data: LineDataPoint[];
  color: string;
  fill?: boolean; // area
  width?: number;
}

interface LineChartProps {
  series: LineSeries[];
  width?: number;
  height?: number;
  showLabels?: boolean;
  /** Force-rotate X-axis labels. `true` = always rotate, `false` = never, omit = auto-detect. */
  rotateLabels?: boolean;
  /** Vertical time-marker annotations overlaid on the chart (e.g. door-open events). */
  annotations?: LineAnnotation[];
}

/** A vertical time-marker annotation to overlay on a line chart. */
export interface LineAnnotation {
  /** Epoch ms (UTC) where the marker should appear on the time axis. */
  atTimeMs: number;
  /** Stroke/fill color for the marker line and flag. */
  color: string;
  /** Short label shown inside the flag (e.g. "Opened"). */
  label: string;
  /** Tooltip text shown on hover. */
  tooltip: string;
}

export function LineChart({ series, width = 500, height = 250, showLabels = true, rotateLabels, annotations }: LineChartProps) {
  const firstSeries = series[0];
  if (series.length === 0 || !firstSeries || firstSeries.data.length === 0) return null;
  const allValues = series.flatMap(s => s.data.map(d => d.value));
  const rawMax = Math.max(...allValues);
  const rawMin = Math.min(...allValues);
  // Compute nice bounds — if all values are non-negative, keep 0 as the floor
  const niceMax = niceRound(Math.max(rawMax, rawMin >= 0 ? 1 : 0));
  const niceMin = rawMin >= 0 ? 0 : niceRound(rawMin);
  const range = niceMax - niceMin || 1;
  const labels = firstSeries.data.map(d => d.label ?? "");
  const maxLabelLen = Math.max(...labels.map((l) => l.length), 0);
  // Dynamic left padding based on longest Y-axis label
  const yTickCount = 5;
  const yLabels = Array.from({ length: yTickCount }, (_, i) => {
    const f = i / (yTickCount - 1);
    const val = niceMin + f * range;
    return range < 10 ? val.toFixed(2) : formatCompact(Math.round(val));
  });
  const maxYLabelLen = Math.max(...yLabels.map(l => l.length), 0);
  const plotLeft = Math.max(50, maxYLabelLen * 10 + 8);
  const plotRight = width - 80;
  const xStepForLabels = labels.length > 1 ? (plotRight - plotLeft) / (labels.length - 1) : (plotRight - plotLeft);
  const rotateXLabels = rotateLabels ?? (maxLabelLen * 6 > xStepForLabels);
  const labelStride = Math.max(1, Math.ceil(labels.length / 20));
  const plotTop = 25;
  const plotBottom = height - (rotateXLabels ? 60 : 30);
  const plotW = plotRight - plotLeft;
  const plotH = plotBottom - plotTop;

  const xStep = labels.length > 1 ? plotW / (labels.length - 1) : plotW;

  // Interpolate annotation instants onto the categorical (index) axis using label timestamps.
  const annotationMarks = (() => {
    if (!annotations || annotations.length === 0) return [];
    // Parse label timestamps into epoch-ms, keep track of valid index/time pairs.
    const valid: { i: number; t: number }[] = [];
    for (let i = 0; i < labels.length; i++) {
      const t = labels[i] ? Date.parse(labels[i]!) : NaN;
      if (!Number.isNaN(t)) valid.push({ i, t });
    }
    if (valid.length < 2) return [];
    const first = valid[0]!, last = valid[valid.length - 1]!;
    const marks: { x: number; color: string; label: string; tooltip: string }[] = [];
    for (const a of annotations) {
      if (a.atTimeMs < first.t || a.atTimeMs > last.t) continue;
      // Binary-search for the surrounding label pair.
      let lo = 0, hi = valid.length - 1;
      while (lo < hi - 1) { const mid = (lo + hi) >> 1; if (valid[mid]!.t <= a.atTimeMs) lo = mid; else hi = mid; }
      const vLo = valid[lo]!, vHi = valid[hi]!;
      const frac = vHi.t === vLo.t ? 0 : (a.atTimeMs - vLo.t) / (vHi.t - vLo.t);
      const xi = vLo.i + frac * (vHi.i - vLo.i);
      marks.push({ x: plotLeft + xi * xStep, color: a.color, label: a.label, tooltip: a.tooltip });
    }
    return marks;
  })();

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: "block" }}>
      {/* Y gridlines */}
      {Array.from({ length: yTickCount }, (_, i) => {
        const f = i / (yTickCount - 1);
        const y = plotBottom - f * plotH;
        const val = niceMin + f * range;
        return (
          <g key={i}>
            <line x1={plotLeft} y1={y} x2={plotRight} y2={y} stroke="var(--color-bg-muted)" />
            <text x={plotLeft - 4} y={y + 4} textAnchor="end" fontSize={15} fill="var(--color-text-muted)">
              {range < 10 ? +val.toFixed(2) : formatCompact(Math.round(val))}
            </text>
          </g>
        );
      })}
      {/* Zero line when range spans negative */}
      {niceMin < 0 && niceMax > 0 && (
        <line
          x1={plotLeft}
          y1={plotBottom - ((0 - niceMin) / range) * plotH}
          x2={plotRight}
          y2={plotBottom - ((0 - niceMin) / range) * plotH}
          stroke="var(--color-text-muted)"
          strokeDasharray="4 3"
          opacity={0.6}
        />
      )}
      {/* X labels */}
      {labels.map((l, i) => {
        if (i % labelStride !== 0 && i !== labels.length - 1) return null;
        const x = plotLeft + i * xStep;
        return (
          <text
            key={i}
            x={x}
            y={plotBottom + (rotateXLabels ? 26 : 12)}
            textAnchor={rotateXLabels ? "end" : "middle"}
            fontSize={13}
            fill="var(--color-text-muted)"
            transform={rotateXLabels ? `rotate(-45, ${x}, ${plotBottom + 26})` : undefined}
          >
            {l}
          </text>
        );
      })}
      {/* Series */}
      {series.map((s, si) => {
        const points = s.data.map((d, i) => {
          const x = plotLeft + i * xStep;
          const y = plotBottom - ((d.value - niceMin) / range) * plotH;
          return { x, y, d };
        });
        const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
        // Solid path excludes the final segment; the last segment is drawn dashed/semi-transparent to mark "in progress"
        const solidPath = points.length > 1
          ? points.slice(0, points.length - 1).map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ")
          : linePath;
        const lastSegmentPath = points.length > 1
          ? `M ${points[points.length - 2]!.x} ${points[points.length - 2]!.y} L ${points[points.length - 1]!.x} ${points[points.length - 1]!.y}`
          : undefined;
        const areaBaseY = niceMin < 0 ? plotBottom - ((0 - niceMin) / range) * plotH : plotBottom;
        const areaPath = s.fill && points.length > 0
          ? `${linePath} L ${points[points.length - 1]!.x} ${areaBaseY} L ${points[0]!.x} ${areaBaseY} Z`
          : undefined;
        // Find the indices of the two highest values in this series
        const topIdx = s.data
          .map((d, i) => ({ value: d.value, i }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 2)
          .map(t => t.i);
        return (
          <g key={si}>
            {areaPath && <path d={areaPath} fill={s.color} opacity={0.15} />}
            <path d={solidPath} fill="none" stroke={s.color} strokeWidth={s.width ?? 2} />
            {lastSegmentPath && (
              <path
                d={lastSegmentPath}
                fill="none"
                stroke={s.color}
                strokeWidth={s.width ?? 2}
                strokeDasharray="6 4"
                opacity={0.4}
              />
            )}
            {points.map((p, i) => {
              const isLast = i === points.length - 1;
              const isMax = topIdx.includes(i);
              const showThisLabel = showLabels && (isMax || isLast);
              // Alternate label placement: first series above, others below
              const above = si === 0;
              const labelY = above ? p.y - 12 : p.y + 18;
              const rotateAngle = above ? -35 : 35;
              return (
                <g key={i}>
                  <circle cx={p.x} cy={p.y} r={5} fill={s.color}>
                    <title>{p.d.tooltip ?? `${p.d.label}: ${p.d.value}`}</title>
                  </circle>
                  {showThisLabel && (
                    <text
                      x={p.x + 4}
                      y={labelY}
                      textAnchor="start"
                      fontSize={12}
                      fontWeight={700}
                      fill="var(--color-text)"
                      transform={`rotate(${rotateAngle}, ${p.x + 4}, ${labelY})`}
                    >
                      {String(p.d.value)}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        );
      })}
      {/* Vertical time-marker annotations (drawn on top of series) */}
      {annotationMarks.map((m, i) => {
        const flagW = Math.max(40, m.label.length * 6 + 10);
        const flagX = Math.min(Math.max(m.x - flagW / 2, plotLeft), plotRight - flagW);
        return (
          <g key={`ann-${i}`} style={{ cursor: "help" }}>
            <title>{m.tooltip}</title>
            <line
              x1={m.x}
              y1={18}
              x2={m.x}
              y2={plotBottom}
              stroke={m.color}
              strokeWidth={1.5}
              strokeDasharray="5 4"
              opacity={0.5}
            />
            <rect x={flagX} y={4} width={flagW} height={13} rx={3} fill={m.color} />
            <text
              x={flagX + flagW / 2}
              y={13}
              textAnchor="middle"
              fontSize={9}
              fontWeight={700}
              fill="#ffffff"
            >
              {m.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ── Horizontal Bar Chart ────────────────────────────────

export interface HBarDatum {
  label: string;
  value: number;
  color?: string;
  tooltip?: string;
}

interface HBarChartProps {
  data: HBarDatum[];
  width?: number;
  height?: number;
  /** Format value for the bar label; defaults to rounding */
  formatValue?: (v: number) => string;
  /** Override the max scale value (skip niceRound). Bars scale relative to this. */
  maxValue?: number;
}

export function HorizontalBarChart({ data, width = 500, height = 400, formatValue, maxValue }: HBarChartProps) {
  if (data.length === 0) return null;
  const max = Math.max(...data.map(d => d.value), 0.01);
  const niceMax = maxValue != null && maxValue > 0 ? maxValue : niceRound(max);
  const labelWidth = Math.min(160, Math.max(80, ...data.map(d => truncLabel(d.label).length * 6.5 + 10)));
  const rightPad = 60;
  const topPad = 10;
  const botPad = 30;
  const gap = 4;
  const barH = Math.max(14, Math.min(28, (height - topPad - botPad) / data.length - gap));
  const chartH = Math.max(height, topPad + botPad + data.length * (barH + gap));
  const plotLeft = labelWidth + 10;
  const plotW = width - plotLeft - rightPad;

  const fmt = formatValue ?? ((v: number) => v % 1 !== 0 ? v.toFixed(1) : String(Math.round(v)));

  return (
    <svg width={width} height={chartH} viewBox={`0 0 ${width} ${chartH}`} style={{ display: "block" }}>
      {[0, 0.25, 0.5, 0.75, 1].map(f => {
        const x = plotLeft + f * plotW;
        const val = niceMax * f;
        return (
          <g key={f}>
            <line x1={x} y1={topPad} x2={x} y2={chartH - botPad} stroke="var(--color-bg-muted)" />
            <text x={x} y={chartH - botPad + 16} textAnchor="middle" fontSize={13} fill="var(--color-text-muted)">
              {val % 1 !== 0 ? val.toFixed(1) : Math.round(val)}
            </text>
          </g>
        );
      })}
      <line x1={plotLeft} y1={topPad} x2={plotLeft} y2={chartH - botPad} stroke="var(--color-border-default)" />
      {data.map((d, i) => {
        const barW = (d.value / niceMax) * plotW;
        const y = topPad + i * (barH + gap);
        return (
          <g key={i}>
            <rect x={plotLeft} y={y} width={Math.max(0, barW)} height={barH} fill={d.color ?? getColor(i)} rx={2}>
              <title>{d.tooltip ?? `${d.label}: ${fmt(d.value)}`}</title>
            </rect>
            <text x={plotLeft - 6} y={y + barH / 2 + 4} textAnchor="end" fontSize={13} fill="var(--color-text)">
              {truncLabel(d.label)}
              <title>{d.label}</title>
            </text>
            <text x={plotLeft + barW + 4} y={y + barH / 2 + 4} textAnchor="start" fontSize={13} fontWeight={700} fill="var(--color-text)">
              {fmt(d.value)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ── Scatter Chart ────────────────────────────────────────

export interface ScatterPoint {
  label: string;
  x: number;
  y: number;
  color?: string;
  tooltip?: string;
}

interface ScatterChartProps {
  data: ScatterPoint[];
  xLabel?: string;
  yLabel?: string;
  width?: number;
  height?: number;
}

export function ScatterChart({ data, xLabel, yLabel, width = 500, height = 400 }: ScatterChartProps) {
  if (data.length === 0) return null;
  const maxX = Math.max(...data.map(d => d.x), 1);
  const maxY = Math.max(...data.map(d => d.y), 1);
  const niceMaxX = niceRound(maxX * 1.1);
  const niceMaxY = niceRound(maxY * 1.1);

  const plotLeft = 65;
  const plotRight = width - 30;
  const plotTop = 20;
  const plotBottom = height - 50;
  const plotW = plotRight - plotLeft;
  const plotH = plotBottom - plotTop;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: "block" }}>
      {[0, 0.25, 0.5, 0.75, 1].map(f => {
        const y = plotBottom - f * plotH;
        const val = Math.round(niceMaxY * f);
        return (
          <g key={`y${f}`}>
            <line x1={plotLeft} y1={y} x2={plotRight} y2={y} stroke="var(--color-bg-muted)" />
            <text x={plotLeft - 6} y={y + 4} textAnchor="end" fontSize={13} fill="var(--color-text-muted)">{val}</text>
          </g>
        );
      })}
      {[0, 0.25, 0.5, 0.75, 1].map(f => {
        const x = plotLeft + f * plotW;
        const val = Math.round(niceMaxX * f);
        return (
          <g key={`x${f}`}>
            <line x1={x} y1={plotTop} x2={x} y2={plotBottom} stroke="var(--color-bg-muted)" />
            <text x={x} y={plotBottom + 16} textAnchor="middle" fontSize={13} fill="var(--color-text-muted)">{val}</text>
          </g>
        );
      })}
      <line x1={plotLeft} y1={plotTop} x2={plotLeft} y2={plotBottom} stroke="var(--color-border-default)" />
      <line x1={plotLeft} y1={plotBottom} x2={plotRight} y2={plotBottom} stroke="var(--color-border-default)" />
      {xLabel && (
        <text x={(plotLeft + plotRight) / 2} y={height - 6} textAnchor="middle" fontSize={15} fill="var(--color-text-muted)">{xLabel}</text>
      )}
      {yLabel && (
        <text x={14} y={(plotTop + plotBottom) / 2} textAnchor="middle" fontSize={15} fill="var(--color-text-muted)"
          transform={`rotate(-90, 14, ${(plotTop + plotBottom) / 2})`}>{yLabel}</text>
      )}
      {data.map((d, i) => {
        const px = plotLeft + (d.x / niceMaxX) * plotW;
        const py = plotBottom - (d.y / niceMaxY) * plotH;
        return (
          <g key={i}>
            <circle cx={px} cy={py} r={8} fill={d.color ?? getColor(i)} opacity={0.85} stroke="#222" strokeWidth={1}>
              <title>{d.tooltip ?? `${d.label}: (${d.x}, ${d.y})`}</title>
            </circle>
            <text x={px} y={py - 12} textAnchor="middle" fontSize={13} fontWeight={600} fill="var(--color-text)">
              {d.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
